import type { AvailableLanguages } from '@cw-elements/profile-dropdown/utils/contants';
import {
  SetCurrentCompany,
  SetCurrentProperty,
  SetCurrentSeasons,
  SetInterfaceLanguage,
  ToggleMonitoringDrawer
} from '@protector/entities/core/__legacy__/redux.actions';
import { clearMemoizedFunction } from '@protector/utils/functions';
import i18n from 'config/i18n';
import { selectPropertiesFromCurrentCompany } from 'core/core.reducer';
import { getInterfaceLanguage, getSystemFlags } from 'core/core.selectors';
import { getCurrentLanguage } from 'core/utils/functions';
import { useFarmHomePage } from 'core/utils/hooks/use-farm-home-page.hook';
import {
  useCompanyOrOrganizationMatch,
  useHallOfWorkspaceMatch,
  useOrganizationMatch,
  useWorkspaceMatch
} from 'core/utils/hooks/usePathMatches';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { getCurrentWorkspaceId, getSelectedCompany, selectHasDiseaseRiskLandingPage } from 'entities/company/company.selectors';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { ClearReports } from 'entities/reports/report.actions';
import { getActiveSeasonsIds } from 'entities/season/season.functions';
import { organizationFarmRoutes } from 'infra/routes/organization-farm.routes';
import useDiseaseRiskStore from 'pages/disease-risk/disease-risk.store';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import useReportsOverlayStore from './reports-overlay.store';
import { useRiskManagementEntry } from './use-risk-management-entry.hook';
import { useUnitSystem } from './use-unit-system.hook';

export const useTopBar = () => {
  const navigate = useNavigate();

  const currentWorkspaceId = useSelector(getCurrentWorkspaceId);
  const currentOrg = useSelector(getSelectedCompany);
  const currentProperty = useSelector(getSelectedProperty);
  const currentLanguage = useSelector(getInterfaceLanguage);
  const systemFlags = useSelector(getSystemFlags);
  const segmentTracking = useSegmentTracking();
  const hasDiseaseRiskLandingPage = useSelector(selectHasDiseaseRiskLandingPage);
  const farmHomePage = useFarmHomePage();
  const properties = useSelector(selectPropertiesFromCurrentCompany);
  const location = useLocation();

  const dispatch = useDispatch();

  const { resetRegions } = useDiseaseRiskStore();
  const openReportsOverlay = useReportsOverlayStore(state => state.open);

  const { workspaceId, companyId, propertyId } = useParams();

  /*
   * The hall of companies is mounted on a splat route, so `useParams` never sees the workspace it
   * is showing; matching the path is the only way to read it there. Without this the reports button
   * could resolve no level at all on that page and fell back to the legacy drawer.
   */
  const routeWorkspaceId = useWorkspaceMatch()?.params?.workspaceId;
  const selectedWorkspaceId = currentWorkspaceId ?? workspaceId ?? routeWorkspaceId;

  /*
   * The organization the path names wins over the one the store remembers: on the hall the splat
   * route hides it from `useParams`, and the store is empty on a cold load and stale after a visit.
   */
  const routeOrgId = useOrganizationMatch()?.params?.companyId;
  const selectedOrgId = routeOrgId ?? currentOrg?.id ?? companyId;
  const selectedPropertyId = currentProperty?.id ?? propertyId;

  const hallOfPropertiesMatch = useCompanyOrOrganizationMatch();
  const isHallOfEntitiesMFE = useOrganizationMatch();
  const hallOfCompaniesMatch = useHallOfWorkspaceMatch();

  const featureConfig = useMemo(() => {
    return {
      hasSettingsMenu: Boolean(hallOfPropertiesMatch),
      hasProfileDropdown: true,
      hasDeckOfApps: true,
      hasAiChatbot: Boolean(systemFlags?.P40_62477_ai_chatbot),
      hideEntitiesDropdown: !hallOfPropertiesMatch || !!isHallOfEntitiesMFE
    };
  }, [hallOfPropertiesMatch, isHallOfEntitiesMFE, systemFlags?.P40_62477_ai_chatbot]);

  const landingPage = useMemo(() => {
    return hasDiseaseRiskLandingPage ? 'disease-risk' : 'timeline';
  }, [hasDiseaseRiskLandingPage]);

  const changeLanguage = (lang: Lowercase<AvailableLanguages>) => {
    if (lang.toLowerCase() === currentLanguage) return;

    const payload = {
      language: lang.toUpperCase(),
      previousLanguage: currentLanguage ?? ''
    };
    segmentTracking.track(`User Settings - Changed Language`, payload);

    dispatch(SetInterfaceLanguage(lang.toLowerCase()));

    void i18n.changeLanguage(lang.toLowerCase());
  };

  const handleCurrentLanguage = (key: AvailableLanguages) => {
    let languageKey: AvailableLanguages = key;

    if (languageKey === 'en') languageKey = 'en-US' as AvailableLanguages;
    if (languageKey === 'en-GB') languageKey = 'en';

    changeLanguage(languageKey.toLocaleLowerCase() as Parameters<typeof changeLanguage>[0]);
    localStorage.setItem('currentLanguage', languageKey);
    clearMemoizedFunction(getCurrentLanguage);
  };

  /**
   * Behind the flag the reports open as a drawer over the current page instead of a page of their
   * own, so the timeline stays visible while exporting (P40-64021). The legacy drawer stays the
   * answer both when the flag is off and when no level could be resolved, so the button is never
   * inert.
   */
  const handleOpenReportDrawer = () => {
    segmentTracking.track('Export Report - Tapped to see Available Reports');

    /*
     * The store remembers the last organization and property the reader opened, and remembering is
     * right nearly everywhere — but on the workspace hall it would send them back to a level they
     * have just stepped out of. Standing on a workspace with no organization in the path, the path
     * is the truth and the memory is stale.
     */
    const onWorkspaceItself = Boolean(routeWorkspaceId) && !isHallOfEntitiesMFE;
    const orgIdForReports = onWorkspaceItself ? undefined : selectedOrgId;
    const propertyIdForReports = onWorkspaceItself ? undefined : selectedPropertyId;
    /* Same resolvability as the old `toReportsListPath`: an org or a workspace, at minimum. */
    const canResolveLevel = Boolean(orgIdForReports) || Boolean(selectedWorkspaceId);

    if (systemFlags?.P40_61669_reports_mfe_preview && canResolveLevel) {
      openReportsOverlay({ companyId: orgIdForReports, propertyId: propertyIdForReports });
      return;
    }

    dispatch(ToggleMonitoringDrawer());
  };

  const onWorkspaceSelect = (workspace: { value: string }) => {
    if (selectedWorkspaceId === workspace.value) {
      return;
    }
    resetRegions();
    dispatch(SetCurrentProperty(null));
    dispatch(SetCurrentCompany(null));
    dispatch(ClearReports());
    navigate(`/hall-of-companies/workspace/${workspace.value}`, {
      state: {
        stopRedirect: true
      }
    });
    segmentTracking.track('Workspace Selector - Changed Workspace');
  };

  const onGoToWorkspaceHall = () => {
    dispatch(SetCurrentProperty(null));
    dispatch(SetCurrentCompany(null));
    dispatch(ClearReports());
    navigate(`/hall-of-companies`, {
      state: {
        stopRedirect: true
      }
    });
  };

  const onOrganizationSelect = (org: { value: string; workspaceId: string }) => {
    if (selectedOrgId === org.value) {
      return;
    }
    resetRegions();
    dispatch(SetCurrentProperty(null));
    dispatch(SetCurrentCompany(org.value));
    dispatch(ClearReports());

    const path = generatePath(`/hall-of-companies/workspace/:workspaceId/organization/:companyId`, {
      companyId: org.value,
      workspaceId: org.workspaceId
    });
    navigate(path);
    segmentTracking.track('Organization Selector - Changed Organization');
  };

  const onGoToHallOfCompanies = () => {
    dispatch(SetCurrentProperty(null));
    dispatch(SetCurrentCompany(null));
    dispatch(ClearReports());
    navigate(`/hall-of-companies/workspace/${currentWorkspaceId}`, {
      state: {
        stopRedirect: true
      }
    });
  };

  const updatePath = (farm: { value: string; orgId: string }, orgChanged: boolean) => {
    if (orgChanged) {
      const path = generatePath(`/company/:companyId/property/:propertyId/${landingPage}`, {
        companyId: farm.orgId,
        propertyId: farm.value
      });
      navigate(path);
      return;
    }
    let redirectPath = location.pathname.split('/').slice(5).join('/');
    if (systemFlags?.P40_64190_fix_shared_routes_levels) {
      const farmMatch = matchPath('/company/:companyId/property/:propertyId/*', location.pathname);
      const organizationMatch = matchPath('/company/:companyId/*', location.pathname);
      const hasFarmCounterpart = Object.values(organizationFarmRoutes).some(route => matchPath(route.organization, location.pathname));
      const routeSuffix = (pathnameBase: string) => location.pathname.slice(pathnameBase.length).replace(/^\/+/, '');
      redirectPath = farmHomePage.page;
      if (farmMatch) {
        redirectPath = routeSuffix(farmMatch.pathnameBase);
      } else if (organizationMatch && hasFarmCounterpart) {
        redirectPath = routeSuffix(organizationMatch.pathnameBase);
      }
    }
    const path = generatePath(`/company/:companyId/property/:propertyId/${redirectPath}`, {
      companyId: farm.orgId,
      propertyId: farm.value
    });
    navigate(path);
  };

  const onFarmSelect = (farm: { value: string; orgId: string }) => {
    if (selectedPropertyId === farm.value) {
      return;
    }

    segmentTracking.track('Property Selector - Changed Property');

    const clickedProperty = properties.find(property => property.id === farm.value);
    dispatch(SetCurrentSeasons(getActiveSeasonsIds(clickedProperty?.seasons)));
    dispatch(SetCurrentProperty(farm.value));
    dispatch(ClearReports());
    resetRegions();

    const orgChanged = selectedOrgId !== farm.orgId;
    updatePath(farm, orgChanged);
  };

  const onGoToHallOfFarms = ({ workspaceId, orgId }: { workspaceId?: string; orgId?: string }) => {
    dispatch(SetCurrentProperty(null));
    dispatch(ClearReports());
    navigate(`/hall-of-companies/workspace/${workspaceId}/organization/${orgId}`, {
      state: {
        stopRedirect: true
      }
    });
  };

  const { handleCurrentUnitSystem } = useUnitSystem();

  const { entitiesSelectorAdditionalData, entitiesSelectorOnNavigate } = useRiskManagementEntry();

  const shouldShowReportIcon = !hallOfCompaniesMatch;

  return {
    onFarmSelect,
    onGoToHallOfFarms,
    onOrganizationSelect,
    onGoToHallOfCompanies,
    handleCurrentLanguage,
    handleOpenReportDrawer,
    handleCurrentUnitSystem,
    onWorkspaceSelect,
    onGoToWorkspaceHall,
    featureConfig,
    shouldShowReportIcon,
    selectedWorkspaceId,
    selectedOrgId,
    selectedPropertyId,
    entitiesSelectorAdditionalData,
    entitiesSelectorOnNavigate
  };
};
